/**
 * comparisonLinkService.js : liens de comparaison (D257), routes du créateur et routes publiques.
 *
 * Créateur : POST / GET / DELETE /api/compare/links. Erreurs rendues par code
 * (`code` du backend), le jeton n'est renvoyé qu'à la création.
 *
 * Public : le jeton vient du fragment (`/comparaison-partagee#<jeton>`), que le
 * navigateur n'envoie jamais ; il voyage dans l'en-tête X-Comparison-Link-Token,
 * sans cookie (`credentials: 'omit'`), comme le lien de séance (D247).
 */
import { authFetch, getAuthHeaders } from '../../utils/authFetch';
import { isRateLimited } from '../../utils/rateLimitMessage';
import { fetchAllReadings } from '../fetchAllReadings';
import { linkError } from './publicSessionLinkService';

export const COMPARISON_LINK_PAGE_PATH = '/comparaison-partagee';
const OWNER_BASE = '/api/compare/links';
const PUBLIC_BASE = '/api/public/compare-link';
const TOKEN_HEADER = 'X-Comparison-Link-Token';

async function parse(res) {
  if (isRateLimited(res)) return { ok: false, code: 'TOO_MANY_REQUESTS' };
  const data = await res.json();
  if (res.ok && data.success) return { ok: true, data };
  return { ok: false, code: data.code || 'SERVER_ERROR', limit: data.limit };
}

async function call(url, options, label) {
  try {
    const res = await authFetch(url, { credentials: 'include', ...options, headers: { ...getAuthHeaders(), ...options.headers } });
    return await parse(res);
  } catch (err) {
    console.error(`${label}:`, err?.message);
    return { ok: false, code: 'SERVER_ERROR' };
  }
}

/**
 * @param {{ sessionIds: string[], label: string, durationDays: 1|7|30,
 *   window: {start:number, end:number}|null, align: 'fromStart'|'clock' }} input
 *   `window` en ms epoch (heure réelle), envoyé en ISO 8601 avec fuseau (`Z`).
 */
export function createComparisonLink({ sessionIds, label, durationDays, window, align }) {
  const body = { sessionIds, label, durationDays, align };
  if (window) {
    body.from = new Date(window.start).toISOString();
    body.to = new Date(window.end).toISOString();
  }
  return call(OWNER_BASE, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }, 'createComparisonLink');
}

export const listComparisonLinks = () => call(OWNER_BASE, {}, 'listComparisonLinks');

export const revokeComparisonLink = (linkId) => call(`${OWNER_BASE}/${linkId}`, { method: 'DELETE' }, 'revokeComparisonLink');

/** Adresse publique du lien : jeton dans le fragment, jamais dans le chemin ni la query. */
export function comparisonLinkUrl(token, origin = window.location.origin) {
  return `${origin}${COMPARISON_LINK_PAGE_PATH}#${token}`;
}

const publicInit = (token) => ({ credentials: 'omit', headers: { [TOKEN_HEADER]: token } });

/** Séances (liste blanche), période, mode d'axe et expiration du lien. */
export async function fetchLinkedComparison(token, signal) {
  const res = await fetch(PUBLIC_BASE, { ...publicInit(token), signal });
  if (!res.ok) throw await linkError(res);
  return res.json();
}

/** Toutes les pages de mesures de la séance de rang `rank` (D235), bornées à la période du lien. */
export async function fetchComparisonReadings(token, rank, signal) {
  try {
    return await fetchAllReadings(null, {
      signal,
      pageUrl: (limit, skip) => `${PUBLIC_BASE}/readings?session=${rank}&limit=${limit}&skip=${skip}`,
      requestInit: publicInit(token),
    });
  } catch (err) {
    if (err?.response) throw await linkError(err.response);
    throw err;
  }
}
